import { createReducer } from "typesafe-actions";

import type {
  FiCatalogResponse,
  FiFocusArea,
  FiLimits,
  FiScopeRequest,
} from "@mapmycustomers/shared/types/entity/fieldInsight";
import type Organization from "@mapmycustomers/shared/types/Organization";

import {
  fetchFieldInsightsCharts,
  fetchFieldInsightsFocusAreas,
  fetchFieldInsightsLimits,
  FieldInsightsActions,
  removeFieldInsightsCharts,
} from "./actions";

export interface FieldInsightsState {
  charts: FiCatalogResponse[];
  chartsFailed: boolean;
  chartsLoading: boolean;
  chartsOrganizationId?: Organization["id"];
  chartsScope?: FiScopeRequest; // scope the catalog was fetched for
  focusAreas: FiFocusArea[];
  focusAreasFailed: boolean;
  focusAreasLoading: boolean;
  focusAreasOrganizationId?: Organization["id"];
  // Absent until the limits read lands, or when it failed — consumers fall back to their defaults.
  limits?: FiLimits;
  limitsFailed: boolean;
  limitsLoading: boolean;
  limitsOrganizationId?: Organization["id"];
}

// chartsLoading starts true so consumers never treat the not-yet-fetched catalog as an empty one.
export const initialFieldInsightsState: FieldInsightsState = {
  charts: [],
  chartsFailed: false,
  chartsLoading: true,
  focusAreas: [],
  focusAreasFailed: false,
  focusAreasLoading: true,
  limitsFailed: false,
  limitsLoading: true,
};

const fieldInsights = createReducer<FieldInsightsState, FieldInsightsActions>(
  initialFieldInsightsState
)
  .handleAction(fetchFieldInsightsCharts.request, (state) => ({
    ...state,
    chartsFailed: false,
    chartsLoading: true,
  }))
  .handleAction(fetchFieldInsightsCharts.success, (state, { payload }) => ({
    ...state,
    charts: payload.charts,
    chartsLoading: false,
    chartsOrganizationId: payload.organizationId,
    chartsScope: payload.scope,
  }))
  .handleAction(fetchFieldInsightsCharts.failure, (state) => ({
    ...state,
    chartsFailed: true,
    chartsLoading: false,
  }))
  .handleAction(fetchFieldInsightsFocusAreas.request, (state) => ({
    ...state,
    focusAreasFailed: false,
    focusAreasLoading: true,
  }))
  .handleAction(fetchFieldInsightsFocusAreas.success, (state, { payload }) => ({
    ...state,
    focusAreas: payload.focusAreas,
    focusAreasLoading: false,
    focusAreasOrganizationId: payload.organizationId,
  }))
  .handleAction(fetchFieldInsightsFocusAreas.failure, (state) => ({
    ...state,
    focusAreasFailed: true,
    focusAreasLoading: false,
  }))
  .handleAction(fetchFieldInsightsLimits.request, (state) => ({
    ...state,
    limitsFailed: false,
    limitsLoading: true,
  }))
  .handleAction(fetchFieldInsightsLimits.success, (state, { payload }) => ({
    ...state,
    limits: payload.limits,
    limitsLoading: false,
    limitsOrganizationId: payload.organizationId,
  }))
  .handleAction(fetchFieldInsightsLimits.failure, (state) => ({
    ...state,
    limitsFailed: true,
    limitsLoading: false,
  }))
  .handleAction(removeFieldInsightsCharts, (state, { payload }) => ({
    ...state,
    charts: state.charts.filter(({ id }) => !payload.includes(id)),
  }));

export default fieldInsights;

export type { FieldInsightsActions } from "./actions";

export * from "./selectors";
